/**
 * Fulfilment transport.
 *
 * A fulfilment is what the warehouse does about an order, and it is its own
 * record rather than a status on the order: an imported order is a mirror of
 * the channel's state and stays one.
 *
 * Three contract facts the screens are built on:
 *
 *   • Allocating reserves stock through Inventory's own primitive, so a
 *     promise larger than what is on the shelf is refused by the stock
 *     position itself — `sales_fulfilment.insufficient_stock`.
 *   • A line is met from ONE position. One that cannot be is refused rather
 *     than silently split across several.
 *   • Shipping is the only move with an effect beyond sales: it consumes the
 *     reservations, reduces the positions by the weighted-average cost of what
 *     left, and hands the ledger a `stock_issued` event to post.
 */
import type { components } from "@erp/api-client";

import { apiFetchOperational, type Page } from "@/lib/api/http";
import { fetchPage, type CursorQueryArgs, type ListFilters } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type FulfilmentStatus = Schemas["FulfilmentStatus"];
export type FulfilmentSummary = Schemas["FulfilmentSummary"];
export type Fulfilment = Schemas["Fulfilment"];
export type FulfilmentLine = Schemas["FulfilmentLine"];
export type FulfilmentAllocateRequest = Schemas["FulfilmentAllocateRequest"];

export const FULFILMENT_STATUSES: readonly FulfilmentStatus[] = [
  "allocated",
  "picking",
  "packed",
  "shipped",
  "cancelled",
];

/** The same graph the database holds, so the screen offers exactly the moves
 *  that will be accepted. Nothing follows `shipped`: after the goods are gone
 *  the correction is a return, not a cancellation. */
export const FULFILMENT_TRANSITIONS: Record<FulfilmentStatus, readonly FulfilmentStatus[]> = {
  allocated: ["picking", "cancelled"],
  picking: ["packed", "cancelled"],
  packed: ["shipped", "cancelled"],
  shipped: [],
  cancelled: [],
};

export interface FulfilmentFilters extends ListFilters {
  status?: FulfilmentStatus;
  sales_order_id?: string;
}

export function listFulfilments(
  filters: FulfilmentFilters,
  args: CursorQueryArgs = {},
): Promise<Page<FulfilmentSummary>> {
  return fetchPage<FulfilmentSummary>("/fulfilments", { ...args, filters });
}

export function getFulfilment(fulfilmentId: string, signal?: AbortSignal): Promise<Fulfilment> {
  return apiFetchOperational<Fulfilment>(`/fulfilments/${encodeURIComponent(fulfilmentId)}`, {
    signal,
  });
}

export function allocateFulfilment(
  body: FulfilmentAllocateRequest,
  idempotencyKey: string,
): Promise<Fulfilment> {
  return apiFetchOperational<Fulfilment>("/fulfilments", {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function transitionFulfilment(
  fulfilmentId: string,
  toStatus: FulfilmentStatus,
  shipping: { carrier?: string; tracking_reference?: string },
  idempotencyKey: string,
): Promise<Fulfilment> {
  return apiFetchOperational<Fulfilment>(
    `/fulfilments/${encodeURIComponent(fulfilmentId)}/transitions`,
    {
      method: "POST",
      body: {
        to_status: toStatus,
        ...(shipping.carrier === undefined || shipping.carrier === ""
          ? {}
          : { carrier: shipping.carrier }),
        ...(shipping.tracking_reference === undefined || shipping.tracking_reference === ""
          ? {}
          : { tracking_reference: shipping.tracking_reference }),
      },
      idempotencyKey,
    },
  );
}

/**
 * The refusals this screen must explain rather than swallow. Each is a rule
 * the server names in the problem `code`, and each has an answer the operator
 * can act on.
 */
export const FULFILMENT_REFUSALS = [
  "sales_fulfilment.insufficient_stock",
  "sales_fulfilment.more_than_ordered",
  "sales_fulfilment.product_has_no_category",
  "sales_fulfilment.transition_forbidden",
] as const;
